import * as THREE from "three"
import { Suspense, useMemo, useRef } from "react"
import { Canvas, useFrame, useLoader } from "@react-three/fiber"
import { EffectComposer, N8AO } from "@react-three/postprocessing"
import { BallCollider, Physics, RigidBody } from "@react-three/rapier"
import { OBJLoader } from "three/examples/jsm/loaders/OBJLoader.js"

THREE.ColorManagement.legacyMode = false
const BRAND_COLOR = "#0625ee"
const SILVER_COLOR = "#c8ccd8"
const capMaterials = [
  new THREE.MeshStandardMaterial({ color: BRAND_COLOR, metalness: 0.15, roughness: 0.45 }),
  new THREE.MeshStandardMaterial({ color: SILVER_COLOR, metalness: 0.92, roughness: 0.18 }),
]

function normalizeGeometry(geometry) {
  geometry.computeBoundingBox()
  const center = new THREE.Vector3()
  geometry.boundingBox.getCenter(center)
  geometry.translate(-center.x, -center.y, -center.z)

  const size = new THREE.Vector3()
  geometry.boundingBox.getSize(size)
  const maxDim = Math.max(size.x, size.y, size.z)
  const s = 4 / maxDim
  geometry.scale(s, s, s)
  return geometry
}

function useCrownCapGeometry() {
  const obj = useLoader(OBJLoader, "/tapacorona.obj")
  return useMemo(() => {
    let geometry
    obj.traverse((child) => {
      if (child.isMesh && !geometry) {
        geometry = child.geometry.clone()
        geometry.groups = child.geometry.groups.map((group) => ({ ...group }))
      }
    })
    return normalizeGeometry(geometry)
  }, [obj])
}

useLoader.preload(OBJLoader, "/tapacorona.obj")

// CONFIGURACIÓN DE PELOTAS
const BAUBLE_CONFIG = {
  cantidad: 18,           // Número de pelotas (10-100)
  tamañoMin: 1,           // Tamaño mínimo (0.1-5)
  tamañoMax: 1.4,           // Tamaño máximo (0.1-5)
  spawnPosition: 'top' // 'center', 'sides', 'top', 'random'
}

// CONFIGURACIÓN DE MOVIMIENTO
const MOVEMENT_CONFIG = {
  velocidadX: -50,        // Velocidad horizontal (-200 a 200)
  velocidadY: -150,       // Velocidad vertical (-300 a 300)
  velocidadZ: -50,        // Velocidad profundidad (-200 a 200)
  damping: 0.55,          // Amortiguación lineal (0-1)
  angularDamping: 0.15,   // Amortiguación angular (0-1)
  friction: 0.2           // Fricción (0-1)
}

function getSpawnPosition(r = THREE.MathUtils.randFloatSpread) {
  switch (BAUBLE_CONFIG.spawnPosition) {
    case 'sides':
      return [r(40), r(20) - 25, r(20) - 10] // Más dispersión horizontal
    case 'top':
      return [r(20), r(20) + 25, r(20) - 10] // Más arriba
    case 'random':
      return [r(30), r(30), r(30)] // Dispersión completa
    default: // 'center'
      return [r(20), r(20) - 25, r(20) - 10] // Original
  }
}

function Bauble({ geometry, vec = new THREE.Vector3(), scale, r = THREE.MathUtils.randFloatSpread }) {
  const api = useRef()

  useFrame((state, delta) => {
    delta = Math.min(0.1, delta)
    api.current.applyImpulse(
      vec
        .copy(api.current.translation())
        .normalize()
        .multiply({ 
          x: MOVEMENT_CONFIG.velocidadX * delta * scale, 
          y: MOVEMENT_CONFIG.velocidadY * delta * scale, 
          z: MOVEMENT_CONFIG.velocidadZ * delta * scale 
        }),
    )
  })
  return (
    <RigidBody 
      linearDamping={MOVEMENT_CONFIG.damping} 
      angularDamping={MOVEMENT_CONFIG.angularDamping} 
      friction={MOVEMENT_CONFIG.friction} 
      position={getSpawnPosition(r)} 
      ref={api} 
      colliders="hull" 
      dispose={null}
    >
      <mesh castShadow receiveShadow scale={scale} geometry={geometry} material={capMaterials} />
    </RigidBody>
  )
}

function Pointer({ vec = new THREE.Vector3() }) {
  const ref = useRef()
  useFrame(({ mouse, viewport }) => {
    vec.lerp({ x: (mouse.x * viewport.width) / 2, y: (mouse.y * viewport.height) / 2, z: 0 }, 0.2)
    ref.current?.setNextKinematicTranslation(vec)
  })
  return (
    <RigidBody position={[100, 100, 100]} type="kinematicPosition" colliders={false} ref={ref}>
      <BallCollider args={[2]} />
    </RigidBody>
  )
}

function Scene() {
  const geometry = useCrownCapGeometry()
  const baubles = [...Array(BAUBLE_CONFIG.cantidad)].map(() => ({
    scale: THREE.MathUtils.randFloat(BAUBLE_CONFIG.tamañoMin, BAUBLE_CONFIG.tamañoMax),
  }))

  return (
    <>
      <ambientLight intensity={1} />
      <spotLight position={[20, 20, 25]} penumbra={1} angle={0.2} color="white" castShadow shadow-mapSize={[512, 512]} />
      <directionalLight position={[0, 5, -4]} intensity={4} />
      <directionalLight position={[0, -15, -0]} intensity={3} color="white" />
      <Physics gravity={[0, 0, 0]}>
        <Pointer />
        {baubles.map((props, i) => (
          <Bauble key={i} geometry={geometry} {...props} />
        ))}
      </Physics>
      <EffectComposer disableNormalPass>
        <N8AO color={BRAND_COLOR} aoRadius={2} intensity={1.15} />
      </EffectComposer>
    </>
  )
}

export const App = () => (
  <Canvas
    shadows
    gl={{ alpha: true, stencil: false, depth: false, antialias: false }}
    camera={{ position: [0, 0, 20], fov: 32.5, near: 1, far: 100 }}
    onCreated={(state) => (state.gl.toneMappingExposure = 1.5)}>
    <Suspense fallback={null}>
      <Scene />
    </Suspense>
  </Canvas>
)
